export interface UploadedDocument {
  id: string;
  title: string;
  company: string;
  department: string;
  createdBy: string;
  createdDate: string;
  documentCount: number;
  status: "completed" | "processing" | "failed";
  categories: string[];
}

const STORAGE_KEY = "uploadedDocuments";

export class DocumentHistoryService {
  // Get all uploaded documents from localStorage
  static getUploadedDocuments(): UploadedDocument[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const documents = JSON.parse(stored);
        //all documents have a valid status
        return documents.map((doc: any) => ({
          ...doc,
          status: doc.status || "completed"
        }));
      }
    } catch (error) {
      console.error("Error parsing stored documents:", error);
    }

    // empty array if nothing stored - this will show the empty state
    return [];
  }

  // Add a new uploaded document
  static addUploadedDocument(
    document: Omit<UploadedDocument, "id">
  ): UploadedDocument {
    const documents = this.getUploadedDocuments();
    const newDocument: UploadedDocument = {
      ...document,
      id: Date.now().toString()
    };

    documents.unshift(newDocument);
    this.saveDocuments(documents);
    return newDocument;
  }

  // Update document status
  static updateDocumentStatus(id: string, status: UploadedDocument["status"]) {
    const documents = this.getUploadedDocuments();
    const updated = documents.map((doc) =>
      doc.id === id ? { ...doc, status } : doc
    );
    this.saveDocuments(updated);
  }

  // Delete a document
  static deleteDocument(id: string) {
    const documents = this.getUploadedDocuments();
    const filtered = documents.filter((doc) => doc.id !== id);
    this.saveDocuments(filtered);
  }

  // Save documents to localStorage
  private static saveDocuments(documents: UploadedDocument[]) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(documents));
    } catch (error) {
      console.error("Error saving documents to localStorage:", error);
    }
  }

  // Generate a batch title based on upload count
  static generateBatchTitle(): string {
    const documents = this.getUploadedDocuments();
    const batchCount = documents.length + 1;
    return `Batch ${batchCount}`;
  }

  // Format current date
  static getCurrentDate(): string {
    const now = new Date();
    return now.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric"
    });
  }
}
